<template>
  <div class="contents">
    <div class="d-flex section-title-with-back">
      <img src="@/assets/images/Icon awesome-pen.png" alt width="22" height="22" />
      <p class="title-bk">生徒所属変更</p>
    </div>
    <div class="d-flex section-subtitle-with-back">
      <a href="/pdf/form_student.pdf" target="_blank">フォームの説明を確認</a>
    </div>
    <div class="container">
      <ValidationObserver v-slot="{ handleSubmit }">
        <form @submit.prevent="handleSubmit(confirmEdited)" ref="form">
          <table class="table table-form">
            <tbody>
              <ChoiceField
                v-model="corporationChoiceFieldMultiModels"
                extraRules="required_select"
                label="法人名（法人ID）"
                placeholder="選択してください"
                resourceType="corporation"
                modelFieldName="corporation"
                :choiceDisable="!allowEditCorporation"
                :childFieldNames="['brand', 'school', 'course', 'tamaiClass']"
                :formData="studentAffiliation"
              />
              <ChoiceField
                v-model="brandChoiceFieldMultiModels"
                extraRules="required_select"
                label="ブランド名（ブランドID）"
                placeholder="選択してください"
                resourceType="brand"
                modelFieldName="brand"
                :choiceDisable="!allowEditBrand"
                :childFieldNames="['school', 'course', 'tamaiClass']"
                :formData="studentAffiliation"
              />
              <ChoiceField
                v-model="schoolChoiceFieldMultiModels"
                extraRules="required_select"
                label="教場名（教場ID）"
                placeholder="選択してください"
                resourceType="school"
                modelFieldName="school"
                :choiceDisable="!allowEditSchool"
                :childFieldNames="['course', 'tamaiClass']"
                :formData="studentAffiliation"
              />
              <ChoiceField
                v-model="courseChoiceFieldMultiModels"
                extraRules="required_select"
                label="コース名（コースID）"
                placeholder="選択して下さい"
                resourceType="course"
                modelFieldName="course"
                :childFieldNames="['tamaiClass']"
                :choiceDisable="isEmpty(studentAffiliation.school)"
                :formData="studentAffiliation"
              />
              <ChoiceField
                v-model="tamaiClassChoiceFieldMultiModels"
                extraRules="required_select"
                label="クラス名（クラスID）"
                placeholder="選択して下さい"
                resourceType="class"
                modelFieldName="tamaiClass"
                :choiceDisable="isEmpty(studentAffiliation.course)"
                :formData="studentAffiliation"
              />
            </tbody>
          </table>
          <div class="text-center">
            <button type="submit" class="btn-confirm">確認</button>
          </div>
        </form>
      </ValidationObserver>
      <div>選択されている生徒ID数：{{ selectedStudentSize }}</div>
      <div class="tab-content">
        <div id="table-1" class="tab-pane active">
          <div class="table-wrapper-scroll-y my-custom-scrollbar">
            <b-table
              ref="student-affiliation-table"
              responsive
              sticky-header
              :items="selectedStudents"
              :fields="fields"
              :per-page="perPage"
              :current-page="currentPage"
            >
              <template v-slot:head()="scope">
                <div class="text-nowrap">
                  <b>{{ scope.label }}</b>
                </div>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import store from '@/store/index.js';
import first from 'lodash/first';
import { ValidationObserver } from 'vee-validate';
import {
  basicCorporationNameIdPresent,
  basicBrandNameIdPresent,
  basicSchoolNameIdPresent,
  basicCourseNameIdPresent,
  basicTamaiClassNameIdPresent,
} from '@/presenters/fields';
import ChoiceField from '@/components/forms/ChoiceField.vue';
import isEmpty from 'lodash/isEmpty';

export default {
  data() {
    var fields = [
      {
        key: 'uid',
        label: '生徒ID',
        sortable: true,
        thAttr: { width: 150 },
      },
      {
        key: 'name',
        label: '生徒名',
        sortable: true,
        thAttr: { width: 150 },
      },
      {
        key: 'nameKana',
        label: '生徒フリガナ',
        sortable: true,
        thAttr: { width: 150 },
      },
    ];
    return {
      fields: fields,
      currentPage: 1,
      perPage: 50,
      studentAffiliation: {},
    };
  },
  components: {
    ValidationObserver,
    ChoiceField,
  },
  beforeRouteEnter(routeTo, routeFrom, next) {
    if (store.state.student.selectedStudents.length == 0) {
      next({ name: 'student' });
    } else {
      next();
    }
  },
  computed: {
    firstStudent() {
      return first(this.selectedStudents);
    },
    corporationChoiceFieldMultiModels() {
      return basicCorporationNameIdPresent(this.studentAffiliation.corporation);
    },
    brandChoiceFieldMultiModels() {
      return basicBrandNameIdPresent(this.studentAffiliation.brand);
    },
    schoolChoiceFieldMultiModels() {
      return basicSchoolNameIdPresent(this.studentAffiliation.school);
    },
    courseChoiceFieldMultiModels() {
      return basicCourseNameIdPresent(this.studentAffiliation.course);
    },
    tamaiClassChoiceFieldMultiModels() {
      return basicTamaiClassNameIdPresent(this.studentAffiliation.tamaiClass);
    },
    selectedStudentSize() {
      return this.selectedStudents.length;
    },
    allowEditCorporation() {
      return this.hasAdminPrivilege;
    },
    allowEditBrand() {
      let allowRole = this.hasAdminPrivilege || this.hasCorporationPrivilege;
      let allowCorporation = !isEmpty(this.studentAffiliation.corporation);
      return allowRole && allowCorporation;
    },
    allowEditSchool() {
      let allowRole = this.hasAdminPrivilege || this.hasCorporationPrivilege || this.hasBrandPrivilege;
      let allowBrand = !isEmpty(this.studentAffiliation.brand);
      return allowRole && allowBrand;
    },
    ...mapState(['student']),
    ...mapState({
      selectedStudents: (state) => state.student.selectedStudents,
    }),
    ...mapState('form', ['prevFormData']),
    ...mapGetters('role', ['hasAdminPrivilege', 'hasCorporationPrivilege', 'hasBrandPrivilege']),
  },
  created() {
    this.studentAffiliation = this.createFreshStudentAffiliation();
  },
  methods: {
    createFreshStudentAffiliation() {
      if (this.student.studentAffiliation) {
        this.$logger.debug('Editing old Student Affiliation: ' + this.student.studentAffiliation);
        return this.student.studentAffiliation;
      } else if (this.prevFormData) {
        this.$logger.debug('Restoring previous form data', this.prevFormData);
        return this.prevFormData;
      } else {
        return {
          corporation: this.firstStudent.corporation,
          brand: this.firstStudent.brand,
          school: this.firstStudent.school,
          course: null,
          tamaiClass: null,
        };
      }
    },
    confirmEdited() {
      this.$logger.debug('Started Editing Student Affiliation: ' + JSON.stringify(this.studentAffiliation));
      this.startEditingStudentAffiliation(this.studentAffiliation);
      this.$router.push({ name: 'student-change-affiliation-confirm' });
    },
    ...mapActions('student', ['startEditingStudentAffiliation']),
  },
};
</script>

<style lang="scss" scoped>
.section-title {
  padding-bottom: 6px;
}
.section-subtitle {
  padding-bottom: 12px;
}
form.inline-form {
  display: inline-block;
}
.btn-confirm {
  margin-left: 0px;
}
</style>
